import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export function statusOf(s) {
  if (s.free) return { status: "success", label: "Free · always on" };
  if (!s.configured) return { status: "neutral", label: "Not configured" };
  if (!s.test) return { status: "warning", label: "Saved · untested" };
  if (s.test.ok) return { status: "success", label: s.test.mode === "live" ? "Verified live" : "Stored · format ok" };
  return { status: "critical", label: "Test failed" };
}

export function relTime(iso) {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function Modal({ title, subtitle, icon: Icon, onClose, busy, testid, children, wide = false }) {
  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
         onClick={() => !busy && onClose()} data-testid={testid}>
      <div className={`w-full ${wide ? "max-w-4xl" : "max-w-lg"} bg-surface border border-border-strong rounded-sm shadow-2xl max-h-[92vh] overflow-y-auto`}
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-sm bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <Icon size={14} />
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold leading-none truncate">{title}</div>
              {subtitle && <div className="text-[10.5px] text-muted mt-1">{subtitle}</div>}
            </div>
          </div>
          <button data-testid={`${testid}-close`} onClick={() => !busy && onClose()} disabled={busy}
                  className="w-7 h-7 flex items-center justify-center hover:bg-surface-2 rounded-sm disabled:opacity-40">
            <X size={14} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function FieldLabel({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-muted font-semibold">{label}</span>
        {hint && <span className="text-[10px] text-subtle font-mono-tabular">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

export const inputCls =
  "w-full bg-bg border border-border px-3 py-2 rounded-sm text-[12px] font-mono-tabular focus:outline-none focus:border-primary transition-colors";

export function InlineMsg({ kind = "error", children, testid }) {
  const cls = kind === "error"
    ? "text-critical bg-critical/5 border-critical/30"
    : "text-success bg-success/5 border-success/30";
  return <div data-testid={testid} className={`text-[11.5px] border rounded-sm p-2.5 leading-snug ${cls}`}>{children}</div>;
}
